import config from '../config';
import { getStorage, setStorage, removeStorage } from 'helpers/storage';

// Session model (E4-2385): the API issues a short-lived access JWT plus an httpOnly refresh
// cookie scoped to /api/auth. The JWT is refreshed on demand only - before a request when it is
// about to expire, or after a 401 that says the bearer was missing/expired. There is deliberately
// no timer: an idle tab must be allowed to hit the server's sliding idle timeout.

export const AUDIENCE = 'portal';
export const TOKEN_ERROR_CODES = ['jwt_required', 'token_expired'];
const REFRESH_LOCK = 'insights-refresh';
const SESSION_KEYS = ['user', 'userJwt', 'tempUser', 'tempUserJwt', 'spoofedUser', 'spoofedUserJwt'];

// Same shape the fetch helpers use: base URL + route + function key.
export const apiUrl = url => {
    const codePrefix = url.indexOf('?') > 0 ? '&' : '?';
    return `${config.apiGateway.URL}${url}${codePrefix}code=${config.apiGateway.CODE}`;
};

// The storage bucket that holds the current session: tempUser for zero/partner/spoof logins,
// user otherwise. Mirrors the pairs set at login in actions/user.
const activeBucket = () => (getStorage('tempUser') && !getStorage('user') ? 'tempUser' : 'user');

// Resolve the JWT for a given guid from whichever bucket that guid belongs to; without a guid,
// the active session's JWT.
export const getJwt = guid => {
    if (!guid) return getStorage(`${activeBucket()}Jwt`) || undefined;
    if (getStorage('spoofedUser') === guid) return getStorage('spoofedUserJwt');
    if (getStorage('user') === guid) return getStorage('userJwt');
    if (getStorage('tempUser') === guid) return getStorage('tempUserJwt');
    return undefined;
};

export const setJwt = (jwt, guid, bucket = activeBucket()) => {
    if (guid) setStorage(bucket, guid);
    setStorage(`${bucket}Jwt`, jwt);
};

export const clearSession = () => SESSION_KEYS.forEach(removeStorage);

const decodePayload = jwt => {
    const part = `${jwt}`.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
};

// Refresh this long before exp: 60 s, or a quarter of the token's lifetime (exp - nbf) when that is
// shorter, so a 1-minute JWT still gets 45 s of use instead of refreshing before every request.
const MAX_REFRESH_MARGIN_SECONDS = 60;
const refreshMarginSeconds = payload => {
    const issued = typeof payload?.nbf === 'number' ? payload.nbf : payload?.iat;
    const lifetime = typeof issued === 'number' ? payload.exp - issued : 0;
    return lifetime > 0 ? Math.min(MAX_REFRESH_MARGIN_SECONDS, lifetime / 4) : MAX_REFRESH_MARGIN_SECONDS;
};

// True when the JWT's exp is within `withinSeconds` (default: refreshMarginSeconds). An
// undecodable token is left for the server to judge (a 401 with token_expired still triggers a refresh).
export const jwtExpiresSoon = (jwt, withinSeconds) => {
    if (!jwt) return false;
    try {
        const payload = decodePayload(jwt);
        const exp = payload?.exp;
        if (typeof exp !== 'number') return false;
        const margin = withinSeconds ?? refreshMarginSeconds(payload);
        return exp * 1000 - Date.now() < margin * 1000;
    } catch {
        return false;
    }
};

const defaultSessionExpiredHandler = () => {
    try {
        if (!window.location.pathname.toLowerCase().startsWith('/login')) window.location.assign('/login');
    } catch {
        // Nowhere to go (tests / non-browser).
    }
};

let sessionExpiredHandler = defaultSessionExpiredHandler;

export const setSessionExpiredHandler = handler => {
    sessionExpiredHandler = handler || defaultSessionExpiredHandler;
};

export class SessionExpiredError extends Error {
    constructor(code) {
        super(`Session expired${code ? ` (${code})` : ''}`);
        this.name = 'SessionExpiredError';
        this.code = code;
    }
}

const postRefresh = async () => {
    const response = await fetch(apiUrl('auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audience: AUDIENCE }),
    });

    if (response.status === 401) {
        const body = await response.json().catch(() => null);
        throw new SessionExpiredError(body?.code);
    }
    if (!response.ok) throw new Error(`auth/refresh failed with ${response.status}`);

    const data = await response.json();
    if (!data?.jwt) throw new SessionExpiredError('no_jwt');

    const bucket = getStorage('tempUser') || getStorage('user') ? activeBucket() : data.spoofing ? 'tempUser' : 'user';
    setJwt(data.jwt, data.guid, bucket);
    return data;
};

// Runs under the cross-tab lock. `before` is the JWT this tab held when it decided to refresh: if
// another tab has since stored a different, still-fresh JWT, use that instead of rotating again.
const refreshUnderLock = (before, force) => {
    const run = () => {
        if (!force) {
            const current = getJwt();
            if (current && current !== before && !jwtExpiresSoon(current)) return { jwt: current };
        }
        return postRefresh();
    };
    return typeof navigator !== 'undefined' && navigator.locks?.request ? navigator.locks.request(REFRESH_LOCK, run) : run();
};

// A refresh that fails after another tab logged in (storage now holds a different JWT) is a stale
// result, not the end of the session: the new login's cookie is the live one, so refresh again
// with it. Only a failure while this tab's own session is still current clears storage.
const retryIfSuperseded = (err, before) => {
    const current = getJwt();
    if (!(err instanceof SessionExpiredError) || !current || current === before) throw err;
    return refreshUnderLock(current, true);
};

let inflight = null;
let inflightForced = false;

/**
 * Refresh the access JWT via the refresh cookie. Single-flight per tab (concurrent callers share
 * one request) and serialised across tabs with the Web Locks API where available.
 *
 * Resolves with the refresh response (the login Profile shape incl. jwt) - or `{ jwt }` when
 * another tab had already refreshed. `force` always calls the endpoint (used on page load, which
 * needs the profile). On a session failure (retried once if another tab logged in meanwhile)
 * the session is cleared and, unless
 * `redirectOnFailure` is false, the user is sent to /login; the promise then rejects.
 * `staleJwt` is the token a failed request carried: if storage already holds a different, fresh
 * one (another request or tab refreshed meanwhile) that is used without calling the endpoint.
 */
export const refreshSession = ({ force = false, redirectOnFailure = true, staleJwt } = {}) => {
    if (!inflight || (force && !inflightForced)) {
        const previous = inflight ? inflight.catch(() => {}) : Promise.resolve();
        const before = staleJwt || getJwt();
        const promise = previous
            .then(() => refreshUnderLock(before, force))
            .catch(err => retryIfSuperseded(err, before))
            .finally(() => {
                if (inflight === promise) {
                    inflight = null;
                    inflightForced = false;
                }
            });
        inflight = promise;
        inflightForced = force;
    }

    return inflight.catch(err => {
        if (err instanceof SessionExpiredError) {
            clearSession();
            if (redirectOnFailure) sessionExpiredHandler(err);
        }
        throw err;
    });
};

const headerValue = (headers, name) => {
    if (!headers) return undefined;
    if (typeof headers.get === 'function') return headers.get(name) ?? undefined;
    return headers[name];
};

const isTokenError = async response => {
    if (response?.status !== 401) return false;
    try {
        const body = await (response.clone ? response.clone() : response).json();
        return TOKEN_ERROR_CODES.includes(body?.code);
    } catch {
        return false;
    }
};

const withAuthorization = (init, jwt) => ({ ...init, headers: { ...init.headers, Authorization: `Bearer ${jwt}` } });

/**
 * fetch() with session handling. The request's `token` header (the profile guid) picks the stored
 * JWT; requests without one, or with an Authorization header that did not come from storage, pass
 * straight through. Before the request a
 * soon-to-expire JWT is refreshed; a 401 carrying jwt_required/token_expired is refreshed once
 * and retried once. Any other 401 (a permission failure) is returned untouched.
 */
export const apiFetch = async (url, init = {}) => {
    const guid = headerValue(init.headers, 'token');
    if (!guid) return fetch(url, init);

    const sentAuth = headerValue(init.headers, 'Authorization');
    const storedJwt = getJwt(guid);
    if (sentAuth && sentAuth !== `Bearer ${storedJwt}`) return fetch(url, init);

    if (storedJwt && jwtExpiresSoon(storedJwt)) {
        await refreshSession().catch(() => {});
    }
    const jwt = getJwt(guid);
    const response = await fetch(url, jwt ? withAuthorization(init, jwt) : init);
    if (!(await isTokenError(response))) return response;

    try {
        await refreshSession({ staleJwt: jwt });
    } catch {
        return response;
    }
    const refreshed = getJwt(guid);
    return fetch(url, refreshed ? withAuthorization(init, refreshed) : init);
};

// axios equivalents, installed by services/index.js.
const setAxiosHeader = (requestConfig, name, value) => {
    if (requestConfig.headers?.set) {
        requestConfig.headers.set(name, value);
    } else {
        requestConfig.headers = { ...requestConfig.headers, [name]: value };
    }
};

export const axiosRequestInterceptor = async requestConfig => {
    try {
        const guid = headerValue(requestConfig.headers, 'token');
        const sentAuth = headerValue(requestConfig.headers, 'Authorization');
        let jwt = getJwt(guid);
        if (!guid || !jwt) return requestConfig;

        // An Authorization header that did not come from storage is the caller's to manage.
        if (sentAuth && sentAuth !== `Bearer ${jwt}`) return requestConfig;
        requestConfig._authManaged = true;

        if (jwtExpiresSoon(jwt)) {
            await refreshSession().catch(() => {});
            jwt = getJwt(guid);
        }
        if (jwt) setAxiosHeader(requestConfig, 'Authorization', `Bearer ${jwt}`);
    } catch (e) {
        // Never block a request over auth-header resolution failure - fall through unmodified.
    }
    return requestConfig;
};

export const axiosAuthErrorInterceptor = axiosInstance => async error => {
    const requestConfig = error?.config;
    const code = error?.response?.data?.code;
    const guid = headerValue(requestConfig?.headers, 'token');
    const sentAuth = headerValue(requestConfig?.headers, 'Authorization');
    if (
        !requestConfig ||
        !guid ||
        requestConfig._authRetried ||
        (sentAuth && !requestConfig._authManaged) ||
        error.response?.status !== 401 ||
        !TOKEN_ERROR_CODES.includes(code)
    ) {
        throw error;
    }

    requestConfig._authRetried = true;
    try {
        await refreshSession({ staleJwt: sentAuth?.replace(/^Bearer /, '') });
    } catch {
        throw error;
    }

    const jwt = getJwt(guid);
    if (jwt) setAxiosHeader(requestConfig, 'Authorization', `Bearer ${jwt}`);
    return axiosInstance(requestConfig);
};
